import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, LinearProgress, List, ListItem, ListItemButton, ListItemText, Paper, Popover, Popper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/MicOutlined';
import StopIcon from '@mui/icons-material/StopOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { aiEdit, fetchAiStatus, suggestShorts, transcribeInstruction, type AiStatus, type AiTurn, type ShortSuggestion, type StudioTool } from '../../services/editingApi';
import type { EditList } from '../../utils/editing/editList';
import { debugLog } from '../../utils/debugLog';
import { formatMs, keepOnly, previewOutputMs } from './editStudioModel';
import { brand } from '../../theme';

interface CommandBarProps {
  editId: string;
  list: EditList;
  /** Applies a proposed list to the undo history (the studio autosaves it, keeping a restore point). */
  onApply: (next: EditList, summary: string) => void;
  onUndo: () => void;
  /** Opens a studio tab (when the assistant says the rest is by hand). */
  onOpenPanel?: (panel: StudioTool) => void;
  /** Makes the transcript (for Shorts and captions), when the plan allows. */
  transcript?: { canStart: boolean; start: () => void };
  /** For tests: how long a recording may run. */
  maxRecordMs?: number;
}

export const EXAMPLES = ['Trim the first 12 seconds and add my logo top right', 'Make a 45-second short about the best part, with captions', 'Put my name at the start for three seconds', 'Add music under the speech'];

/** What the assistant can do, and what it won't: shown under "?". */
export const CAN_DO = [
  'Trim the start or the end, or keep only one part (“keep 1:10 to 1:55”)',
  'Cut the part about something (“remove the bit about the free plan”)',
  'Make a Short of a length about a topic, in 9:16, with captions',
  'Add your logo in a corner, your name as a lower third, or a title',
  'Turn captions on or off, pick highlight or pop captions',
  'Add music from your brand kit, mute or lower the original sound',
  'Say a line at a moment (“say ‘thanks for watching’ at the end”)',
  'Reframe to 9:16, square or 4:5; speed a stretch up or slow it down',
];
export const WONT_DO = [
  'Export, save, delete or send anything: you do that, when you’re happy',
  'Raise the resolution above your plan’s, or use files that aren’t in your brand kit',
  'Change anything you didn’t ask about',
];

const PANEL_LABELS: Record<StudioTool, string> = { cut: 'Cut', text: 'Text', pictures: 'Pictures', captions: 'Captions', audio: 'Sound', frame: 'Frame', export: 'Export' };

/** The API error's code (EditingApiError carries one), or null. */
const codeOf = (error: unknown): string | null => {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
};

const toBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

interface Turn {
  instruction: string;
  reply: string;
  changed: boolean;
  /** The list before this turn changed it, to go back to. */
  before: EditList | null;
  panel: StudioTool | null;
}

/** Text or voice in, a changed edit out, always undoable; Shorts suggested from the transcript. */
const CommandBar: React.FC<CommandBarProps> = ({ editId, list, onApply, onUndo, onOpenPanel, transcript, maxRecordMs = 30000 }) => {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<'asking' | 'listening' | 'hearing' | 'shorts' | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [showEarlier, setShowEarlier] = useState(false);
  /** The reply of turn N and before is closed. */
  const [closedThrough, setClosedThrough] = useState(-1);
  const [problem, setProblem] = useState<{ message: string; code: string | null } | null>(null);
  const [heard, setHeard] = useState<string | null>(null);
  const [shorts, setShorts] = useState<ShortSuggestion[] | null>(null);
  const [help, setHelp] = useState<HTMLElement | null>(null);
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [level, setLevel] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const meterRef = useRef<{ context: AudioContext; timer: number } | null>(null);
  const listRef = useRef(list);
  listRef.current = list;
  const latest = turns.length - 1 > closedThrough ? turns[turns.length - 1] : null;

  useEffect(() => {
    let cancelled = false;
    fetchAiStatus().then((s) => { if (!cancelled) setStatus(s); }).catch((error) => debugLog('AI status failed', error));
    return () => { cancelled = true; };
  }, []);

  const stopMeter = () => {
    if (!meterRef.current) return;
    window.clearInterval(meterRef.current.timer);
    meterRef.current.context.close().catch(() => undefined);
    meterRef.current = null;
    setLevel(0);
  };
  useEffect(() => () => stopMeter(), []);

  const ask = useCallback(async (instruction: string) => {
    setBusy('asking');
    setProblem(null);
    setHeard(null);
    const before = listRef.current;
    try {
      const history: AiTurn[] = turns.slice(-6).map((turn) => ({ instruction: turn.instruction, reply: turn.reply }));
      const result = await aiEdit(editId, { instruction, history });
      setTurns((current) => [...current, { instruction: result.instruction, reply: result.reply, changed: result.changed, before: result.changed ? before : null, panel: result.panel || null }]);
      setStatus((s) => (s ? { ...s, promptsUsed: s.promptsPerMonth - result.promptsLeft } : s));
      if (result.changed) onApply(result.list, result.instruction);
      setText('');
    } catch (error) {
      setProblem({ message: error instanceof Error ? error.message : 'The AI couldn’t answer. Try again.', code: codeOf(error) });
    } finally {
      setBusy(null);
    }
  }, [editId, onApply, turns]);

  const send = () => { if (text.trim()) ask(text.trim()); };

  const startListening = async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || typeof MediaRecorder === 'undefined') {
      setProblem({ message: 'Your browser can’t record here. Type the instruction instead.', code: null });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((type) => MediaRecorder.isTypeSupported(type)) || '';
      const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunks.current = [];
      mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) chunks.current.push(event.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        stopMeter();
        const blob = new Blob(chunks.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        if (blob.size === 0) {
          setBusy(null);
          setProblem({ message: 'Nothing was recorded. Try again.', code: null });
          return;
        }
        // Hear it first, then show the words: nothing changes until they're sent.
        setBusy('hearing');
        try {
          const words = await transcribeInstruction(await toBase64(blob), blob.type);
          setText(words);
          setHeard(words);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        } catch (error) {
          setProblem({ message: error instanceof Error ? error.message : 'That couldn’t be heard. Try again.', code: null });
        } finally {
          setBusy(null);
        }
      };
      recorder.current = mediaRecorder;
      mediaRecorder.start();
      setBusy('listening');
      setProblem(null);
      setHeard(null);
      // A level meter and a countdown, so it's clear the microphone hears you.
      try {
        const AudioCtor: typeof AudioContext | undefined = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtor) {
          const context = new AudioCtor();
          const analyser = context.createAnalyser();
          analyser.fftSize = 512;
          context.createMediaStreamSource(stream).connect(analyser);
          const buffer = new Float32Array(512);
          const started = Date.now();
          const timer = window.setInterval(() => {
            analyser.getFloatTimeDomainData(buffer);
            let max = 0;
            for (let i = 0; i < buffer.length; i += 1) max = Math.max(max, Math.abs(buffer[i]));
            setLevel(Math.min(1, max * 2));
            setSecondsLeft(Math.max(0, Math.ceil((maxRecordMs - (Date.now() - started)) / 1000)));
          }, 100);
          meterRef.current = { context, timer };
        }
      } catch (error) {
        debugLog('No microphone meter', error);
      }
      setSecondsLeft(Math.ceil(maxRecordMs / 1000));
      window.setTimeout(() => { if (recorder.current === mediaRecorder && mediaRecorder.state === 'recording') mediaRecorder.stop(); }, maxRecordMs);
    } catch (error) {
      debugLog('Microphone not available', error);
      setProblem({ message: 'The microphone couldn’t be used. Allow it in your browser, or type the instruction.', code: null });
    }
  };

  const stopListening = () => {
    if (recorder.current && recorder.current.state === 'recording') recorder.current.stop();
  };

  const findShorts = async () => {
    setBusy('shorts');
    setProblem(null);
    try {
      const result = await suggestShorts(editId, { count: 3, targetSeconds: 45 });
      setShorts(result.shorts);
      setStatus((s) => (s ? { ...s, promptsUsed: s.promptsPerMonth - result.promptsLeft } : s));
      if (result.shorts.length === 0) setProblem({ message: 'No good stretches were found. Shorts work best on a few minutes of talk; for a short clip, mark the stretch yourself on the timeline.', code: null });
    } catch (error) {
      setProblem({ message: error instanceof Error ? error.message : 'Shorts couldn’t be suggested.', code: codeOf(error) });
    } finally {
      setBusy(null);
    }
  };

  const applyShort = (short: ShortSuggestion) => {
    const current = listRef.current;
    const fromOut = previewOutputMs(current, short.fromMs);
    const toOut = previewOutputMs(current, Math.max(short.fromMs, short.toMs - 1));
    if (fromOut === null || toOut === null) {
      setProblem({ message: 'That stretch was cut from this edit already. Undo the cut first, or pick another.', code: null });
      return;
    }
    const next = keepOnly(current, fromOut, toOut + 1);
    onApply({ ...next, output: { ...next.output, aspect: '9:16' }, reframe: next.reframe || { focusXPct: 50, focusYPct: 45, mode: 'crop', track: [] } }, `Short: ${short.title}`);
    setShorts(null);
    setTurns((turns0) => [...turns0, { instruction: `Short: ${short.title}`, reply: `Kept ${formatMs(short.fromMs)} – ${formatMs(short.toMs)} as a 9:16 Short: “${short.title}”. Add captions in the Captions tab if you like.`, changed: true, before: current, panel: 'captions' }]);
  };

  const goBackTo = (turn: Turn) => {
    if (!turn.before) return;
    if (!window.confirm('Go back to how the edit was before this instruction? Changes made since then are undone too (Redo brings them back).')) return;
    onApply(turn.before, `Before: ${turn.instruction}`);
  };

  if (status && !status.available) {
    return (
      <Alert severity="info" icon={<AutoAwesomeIcon />} sx={{ mb: 1.5 }}>
        {status.promptsPerMonth === 0 ? 'The AI assistant comes with Pro and Pro Max: tell it what to change, in your words.' : 'The AI assistant isn’t available right now.'}
      </Alert>
    );
  }

  const left = status ? Math.max(0, status.promptsPerMonth - status.promptsUsed) : null;
  const earlier = turns.slice(0, -1);
  return (
    <Box sx={{ mb: 1.5, p: 1, border: `1px solid ${brand.border}`, borderRadius: 3, bgcolor: 'background.paper' }} data-testid="command-bar">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <AutoAwesomeIcon fontSize="small" sx={{ color: brand.copper, ml: 0.5 }} />
        <TextField
          size="small"
          ref={boxRef}
          sx={{ flex: '1 1 320px', minWidth: 0 }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Tell the assistant what to change…"
          value={text}
          onChange={(event) => { setText(event.target.value); if (heard) setHeard(null); }}
          onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }}
          disabled={busy !== null && busy !== 'listening'}
          inputRef={inputRef}
          inputProps={{ 'aria-label': 'Instruction for the assistant', maxLength: 1000, 'data-testid': 'command-input' }}
        />
        {status?.speech ? (
          busy === 'listening' ? (
            <Tooltip title="Stop and hear it"><IconButton color="error" onClick={stopListening} aria-label="Stop recording"><StopIcon /></IconButton></Tooltip>
          ) : (
            <Tooltip title="Say it"><span><IconButton onClick={startListening} disabled={busy !== null} aria-label="Speak an instruction"><MicIcon /></IconButton></span></Tooltip>
          )
        ) : null}
        <Button variant="contained" onClick={send} disabled={busy !== null || !text.trim()} endIcon={busy === 'asking' ? <CircularProgress size={14} color="inherit" /> : <SendIcon />} data-testid="command-send" sx={{ flex: 'none' }}>
          {busy === 'asking' ? 'Thinking' : 'Do it'}
        </Button>
        <Button size="small" onClick={findShorts} disabled={busy !== null} data-testid="find-shorts" sx={{ flex: 'none' }}>{busy === 'shorts' ? 'Looking…' : 'Suggest Shorts'}</Button>
        {left !== null ? <Typography variant="caption" color="text.secondary" sx={{ flex: 'none' }}>{left} of {status?.promptsPerMonth} prompts left this month</Typography> : null}
        {status?.offline ? <Chip size="small" variant="outlined" label="Built-in helper (no AI key)" sx={{ flex: 'none' }} /> : null}
        {earlier.length ? (
          <Button size="small" startIcon={<HistoryIcon />} onClick={() => setShowEarlier((v) => !v)} data-testid="show-earlier" sx={{ flex: 'none' }}>{showEarlier ? 'Hide earlier' : `Earlier (${earlier.length})`}</Button>
        ) : null}
        <Tooltip title="What can I ask?"><IconButton size="small" onClick={(event) => setHelp(event.currentTarget)} aria-label="What the assistant can do"><HelpOutlineIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
      {busy === 'listening' ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, px: 0.5 }} data-testid="listening">
          <Chip size="small" color="error" label={`Listening · ${secondsLeft} s left`} />
          <LinearProgress variant="determinate" value={level * 100} sx={{ flex: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { transition: 'none' } }} aria-label="Microphone level" />
          <Typography variant="caption" color="text.secondary">Tap stop when you’re done</Typography>
        </Stack>
      ) : null}
      {busy === 'hearing' ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, px: 0.5 }}>Hearing it…</Typography> : null}
      {heard ? <Typography variant="caption" sx={{ display: 'block', mt: 0.75, px: 0.5 }} data-testid="heard">Heard: “{heard}”. Check it, then press Do it (or fix a word first).</Typography> : null}
      {/* Examples, while the box is empty and has the focus: a dropdown, so nothing below moves. */}
      <Popper open={focused && !text && !latest && !problem && !shorts && !heard && busy === null} anchorEl={boxRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <Paper elevation={6} sx={{ mt: 0.5, py: 0.5, minWidth: 320 }} data-testid="command-examples">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 0.5 }}>Try</Typography>
          {EXAMPLES.map((example) => (
            <ListItemButton
              key={example}
              dense
              // Keep the focus in the box: the click fills it in.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { setText(example); inputRef.current?.focus(); }}
            >
              <ListItemText primary={example} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          ))}
        </Paper>
      </Popper>
      <Popover open={Boolean(help)} anchorEl={help} onClose={() => setHelp(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ p: 2, maxWidth: 380 }} data-testid="assistant-help">
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>The assistant can</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>{CAN_DO.map((item) => <li key={item}>{item}</li>)}</Box>
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>It won’t</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>{WONT_DO.map((item) => <li key={item}>{item}</li>)}</Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Every change shows in the preview first and can be undone. One instruction is one prompt.</Typography>
        </Box>
      </Popover>
      {problem ? (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          onClose={() => setProblem(null)}
          action={problem.code === 'TRANSCRIPT_REQUIRED' && transcript?.canStart ? <Button color="inherit" size="small" onClick={() => { transcript.start(); setProblem({ message: 'Making the transcript. Ask for Shorts again in a minute.', code: null }); }}>Transcribe</Button> : undefined}
        >
          {problem.message}{problem.code === 'TRANSCRIPT_REQUIRED' && transcript && !transcript.canStart ? ' Transcripts come with Pro Max.' : ''}
        </Alert>
      ) : null}
      <Collapse in={showEarlier} unmountOnExit>
        <List dense disablePadding sx={{ mt: 1 }} data-testid="earlier-turns">
          {earlier.map((turn, index) => (
            <ListItem key={index} disableGutters secondaryAction={turn.before ? <Button size="small" onClick={() => goBackTo(turn)}>Go back to before</Button> : undefined}>
              <ListItemText primary={`“${turn.instruction}”`} secondary={turn.reply} primaryTypographyProps={{ variant: 'body2', noWrap: true }} secondaryTypographyProps={{ variant: 'caption' }} sx={{ pr: 16 }} />
            </ListItem>
          ))}
        </List>
      </Collapse>
      {latest ? (
        <Alert
          severity={latest.changed ? 'success' : 'info'}
          sx={{ mt: 1 }}
          data-testid="command-reply"
          action={(
            <Stack direction="row" spacing={0.5}>
              {latest.panel && onOpenPanel ? <Button color="inherit" size="small" onClick={() => onOpenPanel(latest.panel as StudioTool)} data-testid="open-panel">Open {PANEL_LABELS[latest.panel]}</Button> : null}
              {latest.changed ? <Button color="inherit" size="small" onClick={() => { onUndo(); setClosedThrough(turns.length - 1); }}>Undo</Button> : null}
              {/* With its own buttons an Alert drops the close button, so it comes back here. */}
              <IconButton color="inherit" size="small" aria-label="Close" onClick={() => setClosedThrough(turns.length - 1)} data-testid="close-reply"><CloseIcon fontSize="small" /></IconButton>
            </Stack>
          )}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>“{latest.instruction}”</Typography>
          {latest.reply}{latest.changed ? ' Check the preview; nothing is exported until you say so.' : ''}
        </Alert>
      ) : null}
      {shorts && shorts.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Shorts worth cutting</Typography>
          <List dense disablePadding>
            {shorts.map((short) => (
              <ListItemButton key={`${short.fromMs}-${short.toMs}`} onClick={() => applyShort(short)} sx={{ borderRadius: 1 }} data-testid="short-suggestion">
                <ListItemText primary={`${short.title} · ${formatMs(short.fromMs)} – ${formatMs(short.toMs)}`} secondary={short.reason} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
              </ListItemButton>
            ))}
          </List>
          <Button size="small" onClick={() => setShorts(null)}>Dismiss</Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default CommandBar;
